/**
 * Debug harness fixtures.
 *
 * The client is a pure projection of server snapshots (see GameStateContext), so
 * a whole scenario is just a `{ phase, common, view }` triple fed to
 * `updateSnapshot`. Nothing here talks to the socket or the real state machine —
 * it only crafts the perception. Everything downstream (routing, the phase
 * components, HostControls, the spectator banner) renders off it for free.
 *
 * These are illustrative fixtures, deliberately not exhaustive: enough shape to
 * exercise each screen and its notable variants.
 */

import type {
    PlayerInfo,
    CommonRoomData,
    PhaseView,
    GameSettingsData,
    ArticleInfo,
} from "../protocol";
import type { GamePhase } from "../GameStateContext";

/* ── Sample data ──────────────────────────────────────────────────────── */

/** Stable pool of players. `self` is whichever index the panel points at. */
const POOL: { id: string; name: string }[] = [
    { id: "p1", name: "Ada" },
    { id: "p2", name: "Bruno" },
    { id: "p3", name: "Priya" },
    { id: "p4", name: "Marco" },
    { id: "p5", name: "Nadia" },
    { id: "p6", name: "Theo" },
    { id: "p7", name: "Wei" },
    { id: "p8", name: "Sol" },
];

export const MAX_PLAYERS = POOL.length;

/* Articles arrive whole from the server — title, canonical link and source
 * credit — so the harness has to hand over the same shape. Titles with a space
 * and a non-ASCII character are here on purpose: a URL is something the source
 * states, and the harness shouldn't be the only place that looks tidy. */
const ARTICLES: ArticleInfo[] = [
    "Hatshepsut", "Zinc", "The Great Emu War", "Möbius strip", "Cuttlefish",
].map(title => ({
    title,
    url: `https://en.wikipedia.org/wiki/${encodeURIComponent(title.replace(/ /g, "_"))}`,
    attribution: "Wikipedia · CC BY-SA",
}));

/**
 * A stand-in for the passage the server sends: an article's running words,
 * duplicates and all, lower-cased and stripped of punctuation the way
 * WordTransformerService leaves them.
 *
 * Written as prose and split here rather than typed as a word list, because the
 * point of the pool is that it reads like its source — and a hand-written array
 * of distinct words is exactly what this screen stopped being. Real passages run
 * to a few hundred words; this is enough to fill the column and see how it sets.
 */
const WORDS = `
in the winter of 1932 the australian government went to war with a bird and lost
the emu is a large flightless bird native to australia and after the great war
soldiers were settled on farms in western australia where the wheat grew badly
and the birds arrived in their thousands the farmers asked for help and the
minister of defence sent soldiers with machine guns the birds proved difficult
to hit they scattered at the first shot and ran at speeds the guns could not
follow one observer noted that each pack seemed to have its own leader a large
black plumed bird which stood watch while its fellows fed after a week the
soldiers had fired more than two thousand rounds and killed perhaps a few
hundred birds the operation was withdrawn and the newspapers were unkind the
birds have won the government was told and the war was quietly abandoned
`
    .trim()
    .split(/\s+/);

const ANSWERS = [
    "the great misunderstanding of war",
    "a quiet history of zinc",
    "sudden peace, endless creature",
    "the curious strip of all science",
    "brief and loud, the ancient one",
    "gentle war, modern misunderstanding",
    "all of a small history",
    "the endless möbius of peace",
];

const PROMPT = "the ___ of all misunderstandings";

const DEFAULT_SETTINGS: GameSettingsData = {
    timerDurationSeconds: 120,
    articlesPerPlayer: 3,
    allowArticleReshuffle: true,
    maxReshuffles: 2,
    repickArticlesEachRound: true,
    promptMode: "physical",
};

/* ── Tunable parameters ───────────────────────────────────────────────── */

export interface DebugParams {
    phase: Exclude<GamePhase, "enter_room_code">;
    playerCount: number;   // 2..MAX_PLAYERS
    selfIndex: number;     // which POOL player is "me" (0-based)
    isHost: boolean;       // am I the room host
    spectator: boolean;    // am I sealed out as a spectator this game
    round: number;
    totalRounds: number | null;
    timerSeconds: number;  // 0 = no timer
    /** Per-phase variant. Meaning depends on `phase`; see buildView. */
    stage: string;
}

export const DEFAULT_PARAMS: DebugParams = {
    phase: "waiting_room",
    playerCount: 4,
    selfIndex: 0,
    isHost: true,
    spectator: false,
    round: 1,
    totalRounds: 8,
    timerSeconds: 120,
    stage: "default",
};

/** The variant buttons offered per phase. First entry is the default stage. */
export const STAGES: Partial<Record<DebugParams["phase"], { id: string; label: string }[]>> = {
    prompt_entry: [
        { id: "you-pick", label: "You're the picker" },
        { id: "waiting", label: "Waiting on picker" },
        { id: "revealed", label: "Prompt revealed" },
    ],
    digital_prompt_entry: [
        { id: "you-pick", label: "You're the picker" },
        { id: "waiting", label: "Waiting on picker" },
        { id: "revealed", label: "Prompt revealed" },
    ],
    selecting_articles: [
        { id: "choosing", label: "Choosing" },
        { id: "chosen", label: "Chosen — waiting" },
    ],
    word_picker: [
        { id: "building", label: "Building answer" },
        { id: "submitted", label: "Submitted — waiting" },
    ],
    results: [
        { id: "presenter", label: "You present" },
        { id: "watching", label: "Watching" },
        { id: "pre", label: "Before reveal" },
    ],
    voting: [
        { id: "voting", label: "Voting" },
        { id: "voted", label: "Voted — waiting" },
    ],
};

/* ── Builders ─────────────────────────────────────────────────────────── */

function buildPlayers(p: DebugParams): { players: PlayerInfo[]; self: PlayerInfo } {
    const count = Math.min(Math.max(p.playerCount, 1), MAX_PLAYERS);
    const selfIdx = Math.min(p.selfIndex, count - 1);
    // Host is me if I'm host, else the first player who isn't me.
    const hostIdx = p.isHost ? selfIdx : (selfIdx === 0 ? 1 : 0);

    const players: PlayerInfo[] = POOL.slice(0, count).map((base, i) => ({
        id: base.id,
        name: i === selfIdx ? `${base.name} (you)` : base.name,
        isHost: i === hostIdx,
        isConnected: true,
        role: i === selfIdx && p.spectator ? "spectator" : "player",
    }));

    return { players, self: players[selfIdx] };
}

function buildCommon(p: DebugParams, players: PlayerInfo[]): CommonRoomData {
    const now = Date.now();
    return {
        roomCode: "DBUG",
        players,
        round: p.round,
        totalRounds: p.totalRounds,
        settings: { ...DEFAULT_SETTINGS, timerDurationSeconds: p.timerSeconds || 120 },
        phaseStartTime: p.timerSeconds > 0 ? now : null,
        timeoutTime: p.timerSeconds > 0 ? now + p.timerSeconds * 1000 : null,
    };
}

/** Active (non-spectator) players other than self — who we're "waiting on". */
const othersPlaying = (players: PlayerInfo[], selfId: string) =>
    players.filter(pl => pl.id !== selfId && pl.role !== "spectator");

function buildView(p: DebugParams, players: PlayerInfo[], self: PlayerInfo): PhaseView {
    const others = othersPlaying(players, self.id);
    const stage = p.stage;

    switch (p.phase) {
        case "waiting_room":
            return {
                type: "waiting_room",
                isHost: self.isHost,
                minPlayersMet: players.length >= 2,
                settings: { ...DEFAULT_SETTINGS, timerDurationSeconds: p.timerSeconds || 120 },
            };

        case "prompt_entry": {
            const picker = stage === "waiting" ? (others[0] ?? self) : self;
            return {
                type: "prompt_entry",
                currentRound: p.round,
                picker,
                promptText: stage === "revealed" ? PROMPT : null,
            };
        }

        case "digital_prompt_entry": {
            const picker = stage === "waiting" ? (others[0] ?? self) : self;
            const revealed = stage === "revealed";
            return {
                type: "digital_prompt_entry",
                currentRound: p.round,
                picker,
                cardCount: 3,
                // The server holds the texts until a card is turned, so face-down
                // cards carry nothing here either — only a count.
                pickedIndex: revealed ? 1 : null,
                promptText: revealed ? PROMPT : null,
            };
        }

        case "selecting_articles": {
            const chosen = stage === "chosen" || p.spectator;
            return {
                type: "select_article",
                articles: p.spectator ? [] : chosen ? [] : ARTICLES.slice(0, 3),
                selectedArticle: chosen && !p.spectator ? ARTICLES[0] : null,
                isWaiting: chosen || p.spectator,
                awaitingPlayers: others.slice(0, Math.max(0, others.length - 1)),
                prompt: PROMPT,
                reshuffleAvailable: !chosen && !p.spectator,
            };
        }

        case "word_picker": {
            const submitted = stage === "submitted" || p.spectator;
            return {
                type: "word_picker",
                article: ARTICLES[0],
                prompt: PROMPT,
                words: submitted ? [] : WORDS,
                isWaiting: submitted,
                awaitingPlayers: others.slice(0, Math.max(0, others.length - 1)),
            };
        }

        case "results": {
            // In reveal order — the list's order is the wire contract.
            const revealPlayers = players.filter(pl => pl.role !== "spectator");
            const entries = revealPlayers.map((pl, i) => ({
                answer: ANSWERS[i % ANSWERS.length],
                article: ARTICLES[i % ARTICLES.length],
                playerName: pl.name,
            }));
            const total = revealPlayers.length;
            const isPresenter = stage === "presenter" && !p.spectator;
            return {
                type: "results",
                prompt: PROMPT,
                entries,
                highlightedIndex: stage === "pre" ? -1 : Math.min(1, total - 1),
                isPresenter,
                presenterName: isPresenter ? self.name : (others[0]?.name ?? "Ada"),
            };
        }

        case "voting": {
            // Everyone else's answer is votable; self can't vote for their own.
            const entries: Record<string, string | null> = {};
            players.filter(pl => pl.role !== "spectator").forEach((pl, i) => {
                entries[pl.id] = ANSWERS[i % ANSWERS.length];
            });
            const voted = stage === "voted" || p.spectator;
            return {
                type: "voting",
                entries,
                hasVoted: voted,
                awaitingPlayers: others.slice(0, Math.max(0, others.length - 1)),
            };
        }

        case "voting_results": {
            const revealPlayers = players.filter(pl => pl.role !== "spectator");
            const results: Record<string, number> = {};
            const answers: Record<string, string | null> = {};
            revealPlayers.forEach((pl, i) => {
                // A clear winner, then a descending tail.
                results[pl.id] = Math.max(0, revealPlayers.length - i - (i === 0 ? 0 : 1));
                answers[pl.id] = ANSWERS[i % ANSWERS.length];
            });
            return { type: "voting_results", results, answers };
        }

        case "round_leaderboard": {
            const revealPlayers = players.filter(pl => pl.role !== "spectator");
            const scores: Record<string, number> = {};
            const roundResults: Record<string, number> = {};
            revealPlayers.forEach((pl, i) => {
                scores[pl.id] = (revealPlayers.length - i) * 3 + p.round;
                roundResults[pl.id] = Math.max(0, revealPlayers.length - i - 1);
            });
            return { type: "round_leaderboard", scores, roundResults };
        }

        case "game_over": {
            const revealPlayers = players.filter(pl => pl.role !== "spectator");
            const finalScores: Record<string, number> = {};
            revealPlayers.forEach((pl, i) => {
                finalScores[pl.id] = (revealPlayers.length - i) * 7 + (i === 0 ? 4 : 0);
            });
            return { type: "game_over", finalScores };
        }
    }
}

export interface Snapshot {
    phase: string;
    common: CommonRoomData;
    self: PlayerInfo;
    view: PhaseView;
}

/** Build the full snapshot triple for a set of params. */
export function buildSnapshot(p: DebugParams): Snapshot {
    const { players, self } = buildPlayers(p);
    return {
        phase: p.phase,
        common: buildCommon(p, players),
        self,
        view: buildView(p, players, self),
    };
}

/** Phases offered as jump targets, in game order. */
export const PHASES: DebugParams["phase"][] = [
    "waiting_room",
    "prompt_entry",
    "digital_prompt_entry",
    "selecting_articles",
    "word_picker",
    "results",
    "voting",
    "voting_results",
    "round_leaderboard",
    "game_over",
];

export const PHASE_LABELS: Record<DebugParams["phase"], string> = {
    waiting_room: "Lobby",
    prompt_entry: "Prompt entry",
    digital_prompt_entry: "Digital draw",
    selecting_articles: "Pick article",
    word_picker: "Word picker",
    results: "Results",
    voting: "Voting",
    voting_results: "Vote results",
    round_leaderboard: "Leaderboard",
    game_over: "Game over",
};
